import PropTypes from 'prop-types';

import { useInView } from 'react-intersection-observer';

import { useAppSelector } from 'dux/app/hooks';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import ScaleLoader from 'Components/ScaleLoader';
import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';

import { productsCategories } from 'constants/products';

import SliderAndContent from 'Blocks/SliderAndContent';

import CartV2ProductCTA from 'Containers/CartV2ProductCTA';
import ProductCTA from 'Containers/ProductCTA';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import { formatPriceWithCurrency } from 'utils/currencies';
import useResponsiveVariant from 'utils/useResponsiveVariant';

import { getHasCompletedOneOfConsultation } from 'dux/user/selectors';
import { useFetchProductsList } from 'dux/products/hooks';

const PriceAndCapacityContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  width: 100%;
  min-height: 55px;
  margin-bottom: ${legacyTheme.spacing.s32};

  border-top: 1px solid var(--dark-color, ${theme.colors.neutral[400]});
  border-bottom: 1px solid var(--dark-color, ${theme.colors.neutral[400]});
`;

const FlexBox = styled.div`
  flex: ${({ flex }) => flex};
  display: flex;
  justify-content: center;
`;

const StyledProductCTA = styled(ProductCTA)`
  ${legacyTheme.breakpoints.match('sm')} {
    width: 300px;
  }
  width: 400px;
  align-self: center;
  margin-bottom: 24px;
`;

const StyledCartV2ProductCTA = styled(CartV2ProductCTA)`
  width: 300px;

  ${legacyTheme.breakpoints.up('sm')} {
    width: 400px;
    align-self: center;
    margin-bottom: 24px;
  }
`;

const AccessoriesProductPageHero = ({ content }) => {
  const hasCompletedOneOfConsultation = useAppSelector(getHasCompletedOneOfConsultation);
  const [ref, inView] = useInView();
  const { isLoaded } = useFetchProductsList();
  const { isMobile } = useResponsiveVariant();

  const { product } = content;

  const shouldAddToCartV2 = !hasCompletedOneOfConsultation;

  if (!isLoaded) {
    return null;
  }
  return (
    <SliderAndContent
      ref={ref}
      backgroundColor="beige"
      blockLayoutMargin={isMobile ? '40px 0px' : '120px auto'}
      focusOnSelect={false}
      sliderDataFrom="product-module"
      slides={content.imageCarousel}
    >
      <Typography align="center" color="grey" markupName="h2" paragraph variant="mono3">
        {product.tag}
      </Typography>
      <Typography align="center" data-testid={product.label} paragraph variant="h1r">
        {product.label}
      </Typography>
      <Typography align="center" paragraph variant="p1">
        {product.description}
      </Typography>
      <Typography align="center" paragraph variant="p1">
        {content.subDescription}
      </Typography>
      <PriceAndCapacityContainer>
        <FlexBox flex={1}>
          <Typography markupName="div" variant="p1">
            {content.priceSectionInfo}
          </Typography>
        </FlexBox>
        <FlexBox flex={0}>
          <Spacer
            axis="vertical"
            size={40}
            style={{
              '--color-background': theme.colors.neutral[400],
            }}
          />
        </FlexBox>
        <FlexBox flex={1}>
          <Typography markupName="div" variant="p1">
            {isLoaded ? (
              (product.regularPrice || product.price) &&
              formatPriceWithCurrency({
                // `regularPrice` is the price from API, `price` is the price we are using as a fallback in the content file
                price: product.regularPrice || product.price,
                currency: product.currency,
              })
            ) : (
              <ScaleLoader />
            )}
          </Typography>
        </FlexBox>
      </PriceAndCapacityContainer>
      {shouldAddToCartV2 ? (
        <StyledCartV2ProductCTA item={{ product_type: product.slug }} />
      ) : (
        <StyledProductCTA
          dataFrom="accessories-pdp-cta"
          productCategory={productsCategories.OTHERS}
          slug={product.slug}
          variant="vert"
        />
      )}
      {isMobile &&
        !inView &&
        (shouldAddToCartV2 ? (
          <CartV2ProductCTA fixed item={{ variant_slug: product.slug }} />
        ) : (
          <ProductCTA
            dataFrom="accessories-pdp-cta"
            fixed
            productCategory={productsCategories.OTHERS}
            slug={product.slug}
            variant="vert"
          />
        ))}
      <Typography align="center" color="grey" paragraph variant="p2">
        {content.guarantee}
      </Typography>
    </SliderAndContent>
  );
};

AccessoriesProductPageHero.propTypes = {
  content: PropTypes.shape({
    subDescription: PropTypes.string.isRequired,
    priceSectionInfo: PropTypes.string.isRequired,
    guarantee: PropTypes.string.isRequired,
    imageCarousel: PropTypes.arrayOf(
      PropTypes.shape({
        image: srcOrNextImageProp,
        alt: PropTypes.string,
      })
    ).isRequired,
    product: PropTypes.shape({
      category: PropTypes.string.isRequired,
      currency: PropTypes.string,
      description: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.node.isRequired]),
      shortLabel: PropTypes.string,
      label: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      priceWithSubscription: PropTypes.number,
      displayLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      regularPrice: PropTypes.number,
      slug: PropTypes.string.isRequired,
      tag: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default AccessoriesProductPageHero;
